import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Message } from 'rsuite';
import { useRefreshTimesheetCalendar, useTimesheetCalendar } from '../../hooks/useTimesheet';
import { showToast } from '../../utils/toast';
import { addDays, dateFromIso, formatClock12, formatHM, isoFromDate, nowClock } from '../../utils/timesheet';
import QuickAdd from './QuickAdd';

const LAST_MINUTE = '23:59';

// Asked for by the week, so moving between its days doesn't read the calendar again.
const weekRange = (week) => ({
  start: dateFromIso(week.start).toISOString(),
  end: dateFromIso(addDays(week.end, 1)).toISOString(),
});

// The events on one day, in this browser's time zone, leaving out all-day ones: they mark a day
// rather than time spent. An event running past midnight belongs to the day it starts, and stops
// at the end of that day.
export const eventsForDay = (events, date) => (events || [])
  .filter((e) => !e.isAllDay && isoFromDate(new Date(e.start)) === date)
  .map((e) => {
    const sameDay = isoFromDate(new Date(e.end)) === date;
    const startTime = nowClock(new Date(e.start));
    const endTime = sameDay ? nowClock(new Date(e.end)) : LAST_MINUTE;
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    return { ...e, startTime, endTime, seconds: ((endHour - startHour) * 60 + endMinute - startMinute) * 60 };
  })
  .sort((a, b) => a.startTime.localeCompare(b.startTime));

// Already logged, going by the same start and the note it was given. An entry since reworded
// no longer counts, which only means the button is back.
const isLogged = (event, entries) => entries.some((entry) => entry.description === event.subject
  && entry.startTime === event.startTime);

/**
 * The person's Outlook events for the day, under the day's entries, each with a button to log
 * time from it through the quick add: times and note filled in, the project left to pick. Read
 * only while this is on screen, from the cache where it can be; Refresh goes back to Outlook.
 */
export default function CalendarEvents({ date, week, entries, tasks, config, onQuickAdd }) {
  const range = useMemo(() => weekRange(week), [week]);
  const calendar = useTimesheetCalendar(range);
  const refresh = useRefreshTimesheetCalendar();
  const dayEvents = useMemo(() => eventsForDay(calendar.data?.events, date), [calendar.data, date]);

  // The event being logged and its button, which toggles it.
  const [adding, setAdding] = useState(null); // { event, anchor }
  const addingRef = useRef(null);
  addingRef.current = adding;
  const closeAdding = useCallback(() => {
    addingRef.current?.anchor?.focus?.();
    setAdding(null);
  }, []);
  const toggleAdding = (event, anchor) => {
    setAdding((open) => (open?.event.id === event.id ? null : { event, anchor }));
  };

  // A stale answer means Outlook couldn't be reached, which the warning below already says.
  const refreshCalendar = () => refresh.mutate(range, {
    onSuccess: (data) => {
      if (!data?.stale) showToast('success', 'Calendar refreshed.');
    },
  });

  const fetchedAt = calendar.data?.fetchedAt;
  const error = refresh.error || calendar.error;
  const busy = calendar.isLoading || refresh.isPending;

  return (
    <section className="ts-agenda ts-calendar" aria-label="Outlook calendar">
      <div className="ts-agenda__head">
        <h2 className="ts-agenda__count">
          Outlook calendar
          {dayEvents.length ? <span className="ts-calendar__count">{dayEvents.length}</span> : null}
        </h2>
        <span className="ts-calendar__tools">
          {fetchedAt ? <span className="ts-calendar__updated">Updated {formatClock12(nowClock(new Date(fetchedAt)))}</span> : null}
          <button type="button" className="bdc-btn bdc-btn--sm" onClick={refreshCalendar} disabled={busy}>
            <i className={`bi bi-arrow-repeat${busy ? ' spin' : ''}`} aria-hidden="true" />
            Refresh calendar
          </button>
        </span>
      </div>

      {error ? <Message type="error" showIcon>{String(error?.message || error)}</Message> : null}
      {calendar.data?.stale || refresh.data?.stale ? (
        <Message type="warning" showIcon>Outlook couldn&rsquo;t be reached, so this is the last copy we have.</Message>
      ) : null}

      {calendar.isLoading ? (
        <p className="ts-calendar__empty">Loading your calendar…</p>
      ) : !calendar.error && dayEvents.length === 0 ? (
        <p className="ts-calendar__empty">No calendar events on this day.</p>
      ) : (
        <ul className="ts-calendar__list">
          {dayEvents.map((event) => {
            const logged = isLogged(event, entries);
            return (
              <li key={event.id} className="ts-calendar__row">
                <span className="ts-calendar__time">
                  <strong>{formatClock12(event.startTime)}</strong>
                  <span>{formatClock12(event.endTime)}</span>
                </span>
                <span className="ts-calendar__body">
                  <span className="ts-calendar__subject">{event.subject}</span>
                  {event.location ? <span className="ts-calendar__location">{event.location}</span> : null}
                </span>
                {event.seconds ? <span className="ts-calendar__length">{formatHM(event.seconds)}</span> : null}
                {logged ? (
                  <span className="ts-calendar__logged">
                    <i className="bi bi-check-circle-fill" aria-hidden="true" />
                    Added
                  </span>
                ) : (
                  <button
                    type="button"
                    className="bdc-btn bdc-btn--sm"
                    aria-expanded={adding?.event.id === event.id}
                    aria-label={`Add entry from ${event.subject}`}
                    onClick={(e) => toggleAdding(event, e.currentTarget)}
                  >
                    <i className="bi bi-plus-lg" aria-hidden="true" />
                    Add entry
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {adding ? (
        <QuickAdd
          key={adding.event.id}
          date={date}
          anchor={adding.anchor}
          below
          tasks={tasks}
          config={config}
          prefill={{ description: adding.event.subject, startTime: adding.event.startTime, seconds: adding.event.seconds }}
          onAdd={onQuickAdd}
          onClose={closeAdding}
        />
      ) : null}
    </section>
  );
}
